import React from 'react';
import { Calendar, Clock, MessageSquare, AlignLeft, ExternalLink } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import TaskPriorityBars from './TaskPriorityBars';
import TaskAssigneeChip from '../TaskAssigneeChip';
import SubtasksIcon from './SubtasksIcon';

/**
 * The meta row of a Kanban card (Boards Redesign card 2.4), indented under the
 * title: priority signal → due chip → subtasks "done/total" → done date →
 * comment count → description mark on the left, the 20 px assignee avatar on
 * the right. What shows is decided by `cardMetaOf` (taskCardMeta.js); this
 * only draws it.
 */
export default function TaskCardMeta({ meta, task, onChange, assignee = null, members = [], currentUserId = null, onAssign = null, onOpenDetail = null }) {
  const comments = meta.commentCount;
  const subs = meta.subtasks || { done: 0, total: 0 };
  const subsLabel = `${subs.done} of ${subs.total} subtask${subs.total === 1 ? '' : 's'} done`;
  return (
    <div className="task-card-meta">
      <div className="task-card-meta-left">
        {meta.showPriority && (
          <TaskPriorityBars
            className="task-board-card-priority"
            priority={meta.priority}
            onCycle={(next) => onChange({ priority: next })}
          />
        )}
        {meta.due && (
          <Tooltip content={meta.due.overdue ? `Overdue — due ${meta.due.full}` : `Due ${meta.due.full}`} side="top">
            <span
              className={`task-card-due${meta.due.overdue ? ' task-card-due--overdue' : ''}`}
              aria-label={meta.due.overdue ? `Overdue: due ${meta.due.full}, ${meta.due.daysLate} days late` : `Due ${meta.due.full}`}
              data-overdue={meta.due.overdue ? 'true' : 'false'}
            >
              {meta.due.overdue ? <Clock size={11} aria-hidden /> : <Calendar size={11} aria-hidden />}
              {meta.due.text}
            </span>
          </Tooltip>
        )}
        {subs.total > 0 && (
          <Tooltip content={subsLabel} side="top">
            <span
              className={`task-card-count task-card-subtasks${subs.done === subs.total ? ' task-card-subtasks--complete' : ''}`}
              aria-label={subsLabel}
              data-subtasks={`${subs.done}/${subs.total}`}
            >
              <SubtasksIcon size={11} />
              {subs.done}/{subs.total}
            </span>
          </Tooltip>
        )}
        {meta.doneDate && (
          <Tooltip content={`${meta.doneDate.word === 'dismissed' ? 'Dismissed' : 'Done'} ${meta.doneDate.full}`} side="top">
            <span className="task-card-done-date" data-done-date="true">{meta.doneDate.text}</span>
          </Tooltip>
        )}
        {comments > 0 && (
          <Tooltip content={`${comments} comment${comments === 1 ? '' : 's'}`} side="top">
            <span className="task-card-count" aria-label={`${comments} comment${comments === 1 ? '' : 's'}`}>
              <MessageSquare size={11} aria-hidden />
              {comments}
            </span>
          </Tooltip>
        )}
        {meta.hasDescription && (
          <Tooltip content="Has a description" side="top">
            <span className="task-card-desc-mark" role="img" aria-label="Has a description">
              <AlignLeft size={12} aria-hidden />
            </span>
          </Tooltip>
        )}
        {/* Quick open (owner, 13.10.2026): the pen next to the description
            mark opens the task window, like Edit in the ⋯ menu. Its own
            pointer-down is stopped so dnd-kit does not arm a drag. */}
        {onOpenDetail && (
          <Tooltip content="Open task" side="top">
            <button
              type="button"
              className="task-card-open-btn"
              aria-label="Open task"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail();
              }}
            >
              <ExternalLink size={11} aria-hidden />
            </button>
          </Tooltip>
        )}
      </div>
      {meta.showAssignee && (
        <TaskAssigneeChip
          assigneeId={task.assigneeId}
          member={assignee}
          members={members}
          currentUserId={currentUserId}
          onAssign={onAssign}
          size={20}
        />
      )}
    </div>
  );
}
